import {
  COMPASS_INPUT_STEPS,
  COMPASS_STEP_NAMES,
  COMPASS_STEPS,
} from '@/components/flows/CompassFlow/constants'
import {
  CompassStep,
  PersonalAssetsInput,
} from '@/components/flows/CompassFlow/types'
import { COMPASS_REPORT_STATUS } from '@/lib/constants'
import { CompassReportStatus } from '@/types/misc'

class CompassFlowService {
  isReadOnlyMode(status?: CompassReportStatus): boolean {
    return status === COMPASS_REPORT_STATUS.COMPLETE
  }

  isEditMode(status?: CompassReportStatus): boolean {
    return status !== COMPASS_REPORT_STATUS.COMPLETE
  }

  shouldShowRentalIncomeQuestion(options: {
    personalAssets?: PersonalAssetsInput
    isForPartner?: boolean
  }): boolean {
    const { personalAssets, isForPartner = false } = options

    // If the user and their partner have a joint rental property, we always
    // want to ask both for their rental incomes (even if one of them said
    // they don't own a rental property in their own name).
    if (personalAssets?.joint_has_rental_property) {
      return true
    }

    return isForPartner
      ? !!personalAssets?.partner_has_rental_property
      : !!personalAssets?.user_has_rental_property
  }

  isAdvancedCompassInputStep(step: string | null): step is CompassStep {
    return (
      COMPASS_INPUT_STEPS.includes(step as CompassStep) &&
      step !== COMPASS_STEPS.clientDetails
    )
  }

  isValidInitialCompanyUserCompassStep(
    step: string | null,
  ): step is CompassStep {
    if (this.isReadOnlyMode()) {
      return true
    }

    return (
      this.isValidInitialUserCompassStep(step) ||
      [COMPASS_STEPS.requiredDetails, COMPASS_STEPS.inviteClients].includes(
        step as CompassStep,
      )
    )
  }

  // We only want to allow users to resume Compass from one of the info steps
  // or the client details step. Letting them resume from a step beyond the client
  // details will cause subtle issues (e.g., data in the prior input steps might
  // have become invalid by recent edits so validation errors won't show).
  isValidInitialUserCompassStep(step: string | null): step is CompassStep {
    return (
      this.isValidCompassStep(step) && !this.isAdvancedCompassInputStep(step)
    )
  }

  isValidCompassStep(step: string | null): step is CompassStep {
    return !!step && COMPASS_STEP_NAMES.includes(step as CompassStep)
  }
}

const compassFlowService = new CompassFlowService()

export default compassFlowService
